/**
 * =============================================================================
 * SPEQ55 RFP Pro - Feedback Page
 * =============================================================================
 *
 * Feedback and issue reporting page for authenticated users within the RFP Pro
 * application. This page allows users to submit bug reports, feature requests,
 * or general feedback with optional screenshot attachments.
 *
 * FEATURES:
 * ---------
 * - Three feedback types: Bug Report, Feature Request, General Feedback
 * - Subject and description text fields with validation
 * - Screenshot upload with drag-and-drop preview (up to 5 images, 5MB each)
 * - Real-time character count for description
 * - Loading state during submission
 * - Success/error feedback messages
 *
 * DATA FLOW:
 * ----------
 * 1. User fills out the feedback form
 * 2. Optional: User attaches screenshot images
 * 3. Form data is sent to POST /api/feedback as multipart/form-data
 * 4. Backend saves feedback to database and emails support@speq55.com
 * 5. User sees success confirmation
 *
 * ACCESSIBILITY:
 * --------------
 * - All form fields have associated labels
 * - Required fields are marked with asterisks
 * - Error messages are clearly displayed
 * - Buttons have appropriate disabled states
 *
 * @module pages/Feedback
 * @author SPEQ55
 * @see {@link backend/src/routes/feedback.js} for API endpoint
 */

import { useState, useRef } from 'react';
import api from '../api/client';

// =============================================================================
// FEEDBACK TYPE OPTIONS
// =============================================================================

/**
 * Available feedback type options for the dropdown selector.
 * Each type has a value (sent to API), label (displayed to user),
 * and description (additional context).
 *
 * @constant {Array<Object>}
 */
const feedbackTypes = [
  {
    value: 'bug_report',
    label: 'Bug Report',
    description: 'Report a problem or issue',
  },
  {
    value: 'feature_request',
    label: 'Feature Request',
    description: 'Suggest a new feature',
  },
  {
    value: 'general',
    label: 'General Feedback',
    description: 'Share your thoughts',
  },
];

// =============================================================================
// FEEDBACK COMPONENT
// =============================================================================

/**
 * Feedback page component for submitting user feedback.
 *
 * This component manages three types of state:
 * 1. Form data (feedbackType, subject, description)
 * 2. Screenshot files and their preview URLs
 * 3. Submission status (loading, success, error)
 *
 * @returns {JSX.Element} Feedback page with form and file upload
 */
function Feedback() {
  // ---------------------------------------------------------------------------
  // Form State
  // ---------------------------------------------------------------------------
  // Stores the text field values for the feedback form

  const [formData, setFormData] = useState({
    feedbackType: 'general', // Default to general feedback
    subject: '', // Brief summary of the feedback
    description: '', // Detailed description
  });

  // ---------------------------------------------------------------------------
  // Screenshot State
  // ---------------------------------------------------------------------------
  // screenshots: Array of File objects to be uploaded
  // previews: Array of preview objects with URL for display and metadata
  // fileInputRef: Reference to hidden file input for programmatic access

  const [screenshots, setScreenshots] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);

  // ---------------------------------------------------------------------------
  // Submission State
  // ---------------------------------------------------------------------------
  // isSubmitting: True while API request is in progress (shows loading spinner)
  // submitStatus: 'success' | 'error' | null - determines which message to show
  // errorMessage: Specific error message from API or validation

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  /**
   * Handle form input changes for text fields.
   * Updates the corresponding field in formData state using the input's name attribute.
   *
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement>} e - Change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Handle screenshot file selection from the file input.
   *
   * Validates each selected file for:
   * - Maximum count (5 total screenshots)
   * - File type (must be an image)
   * - File size (max 5MB per image)
   *
   * Creates object URLs for valid files to display previews.
   * Invalid files are skipped with an error message shown to the user.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - File input change event
   */
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    // Validate total count including existing screenshots
    if (screenshots.length + files.length > 5) {
      setErrorMessage('Maximum 5 screenshots allowed');
      return;
    }

    // Arrays to collect valid files and their previews
    const validFiles = [];
    const newPreviews = [];

    // Validate each file individually
    for (const file of files) {
      // Validate file type - must be an image
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Only image files are allowed');
        continue; // Skip invalid file, continue with others
      }

      // Validate file size - max 5MB (5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('Each image must be less than 5MB');
        continue; // Skip oversized file, continue with others
      }

      // File is valid - add to arrays
      validFiles.push(file);
      newPreviews.push({
        file,
        url: URL.createObjectURL(file), // Create blob URL for preview display
        name: file.name,
      });
    }

    // Append valid files to existing state
    setScreenshots((prev) => [...prev, ...validFiles]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    setErrorMessage(''); // Clear any previous error

    // Reset file input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Remove a screenshot from the upload list.
   * Revokes the object URL to prevent memory leaks.
   *
   * @param {number} index - Index of the screenshot to remove
   */
  const removeScreenshot = (index) => {
    // Revoke the object URL to free browser memory
    // This is important to prevent memory leaks when creating many previews
    URL.revokeObjectURL(previews[index].url);

    // Remove from both arrays by filtering out the index
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Handle form submission.
   *
   * Workflow:
   * 1. Prevent default form submission
   * 2. Set loading state
   * 3. Build FormData with text fields and screenshot files
   * 4. POST to /api/feedback endpoint
   * 5. On success: show message, reset form, clean up preview URLs
   * 6. On error: show error message from API or generic fallback
   *
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // Build FormData for multipart/form-data request
      // This is required because we're uploading files along with text fields
      const formDataToSend = new FormData();
      formDataToSend.append('feedbackType', formData.feedbackType);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('description', formData.description);

      // Append each screenshot file with the same field name
      // The backend expects an array of files under 'screenshots'
      screenshots.forEach((file) => {
        formDataToSend.append('screenshots', file);
      });

      // Submit to the feedback API endpoint
      // The api client automatically adds the auth token
      await api.post('/feedback', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Submission successful
      setSubmitStatus('success');

      // Reset form to initial state
      setFormData({
        feedbackType: 'general',
        subject: '',
        description: '',
      });

      // Clean up: revoke all preview object URLs to free memory
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
      setScreenshots([]);
      setPreviews([]);
    } catch (error) {
      // Show error state with message from API or fallback
      setSubmitStatus('error');
      setErrorMessage(
        error.response?.data?.error || 'Failed to submit feedback. Please try again.'
      );
    } finally {
      // Always reset loading state, whether success or error
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-accent-800">Submit Feedback</h1>
        <p className="mt-2 text-accent-500">
          Help us improve RFP Pro by sharing your feedback, reporting bugs, or suggesting new
          features.
        </p>
      </div>

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-secondary-50 border border-secondary-200 rounded-lg">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-secondary-500 mr-2 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-secondary-700">
              Thank you! Your feedback has been submitted successfully. We appreciate your input.
            </p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {(submitStatus === 'error' || errorMessage) && (
        <div className="mb-6 p-4 bg-error-light border border-error rounded-lg">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-error mr-2 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-error-dark">
              {errorMessage || 'Something went wrong. Please try again.'}
            </p>
          </div>
        </div>
      )}

      {/* Feedback Form */}
      <form onSubmit={handleSubmit} className="card">
        <div className="card-body space-y-6">
          {/* Feedback Type */}
          <div>
            <label
              htmlFor="feedbackType"
              className="block text-sm font-medium text-accent-700 mb-2"
            >
              Feedback Type
            </label>
            <select
              id="feedbackType"
              name="feedbackType"
              value={formData.feedbackType}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-2 border border-accent-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed"
            >
              {feedbackTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.description}
                </option>
              ))}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-accent-700 mb-2">
              Subject <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              required
              maxLength={255}
              value={formData.subject}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-2 border border-accent-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed"
              placeholder="Brief summary of your feedback"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-accent-700 mb-2">
              Description <span className="text-error">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows={6}
              maxLength={10000}
              value={formData.description}
              onChange={handleChange}
              disabled={isSubmitting}
              className="w-full px-4 py-2 border border-accent-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-accent-100 disabled:cursor-not-allowed resize-none"
              placeholder="Please provide as much detail as possible..."
            />
            <p className="mt-1 text-sm text-accent-400">
              {formData.description.length}/10000 characters
            </p>
          </div>

          {/* Screenshots */}
          <div>
            <label className="block text-sm font-medium text-accent-700 mb-2">
              Screenshots (optional)
            </label>
            <p className="text-sm text-accent-500 mb-3">
              Attach up to 5 screenshots to help illustrate your feedback. Max 5MB per image.
            </p>

            {/* Screenshot Previews */}
            {previews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {previews.map((preview, index) => (
                  <div
                    key={index}
                    className="relative group rounded-lg overflow-hidden border border-accent-200"
                  >
                    <img
                      src={preview.url}
                      alt={preview.name}
                      className="w-full h-24 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeScreenshot(index)}
                      disabled={isSubmitting}
                      className="absolute top-1 right-1 p-1 bg-error text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity disabled:cursor-not-allowed"
                      aria-label="Remove screenshot"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                      <p className="text-xs text-white truncate">{preview.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* File Input */}
            {screenshots.length < 5 && (
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  multiple
                  onChange={handleFileSelect}
                  disabled={isSubmitting}
                  className="hidden"
                  id="screenshot-input"
                />
                <label
                  htmlFor="screenshot-input"
                  className={`inline-flex items-center px-4 py-2 border border-accent-300 rounded-lg text-sm font-medium text-accent-700 bg-white hover:bg-accent-50 cursor-pointer transition-colors ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Add Screenshot
                </label>
                <span className="text-sm text-accent-400">{screenshots.length}/5 screenshots</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4 border-t border-accent-200">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-400 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Feedback'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Feedback;
